import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  FolderOpen,
  Check,
  Loader2,
  AlertCircle,
  Plane,
  Building,
  UtensilsCrossed,
  Handshake,
  Calendar as CalendarIcon,
  Car,
  Users,
  StickyNote
} from 'lucide-react';
import DatePickerButton from './DatePickerButton';

const COMPONENT_ICONS = {
  commercial_flight: Plane,
  private_flight: Plane,
  flight: Plane,
  hotel: Building,
  restaurant: UtensilsCrossed,
  meeting: Handshake,
  event: CalendarIcon,
  rental_car: Car,
  car_rental: Car,
  chauffeur_service: Car,
  memo: StickyNote,
};

const COMPONENT_COLORS = {
  commercial_flight: 'text-ui-link',
  private_flight: 'text-ui-violet-fg',
  hotel: 'text-ui-green-fg',
  restaurant: 'text-ui-orange-fg',
  meeting: 'text-ui-indigo-fg',
  event: 'text-ui-pink-fg',
  rental_car: 'text-ui-teal-fg',
  car_rental: 'text-ui-teal-fg',
  chauffeur_service: 'text-ui-ink-3',
  memo: 'text-ui-amber-fg',
};

const BundleComponentForm = ({
  tripId,
  component,       // existing component for edit mode
  tripComponents,  // all components in the trip (for member picker)
  onSuccess,
  onCancel,
}) => {
  const isEditMode = !!component;

  const [title, setTitle] = useState(component?.title || '');
  const [description, setDescription] = useState(component?.description || '');
  const [startDate, setStartDate] = useState(component?.start_datetime?.split('T')[0] || '');
  const [startTime, setStartTime] = useState(
    component?.start_datetime?.includes('T')
      ? component.start_datetime.split('T')[1]?.substring(0, 5) || ''
      : ''
  );
  const [dateIsTbd, setDateIsTbd] = useState(component?.date_is_tbd || false);
  const [timeIsTbd, setTimeIsTbd] = useState(component?.time_is_tbd || false);
  const [selectedChildIds, setSelectedChildIds] = useState(() => {
    if (component?.component_data?.child_component_ids) {
      return new Set(component.component_data.child_component_ids);
    }
    return new Set();
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Available components for the member picker — exclude bundles and already-bundled items
  const availableComponents = useMemo(() => {
    if (!tripComponents) return [];
    return tripComponents.filter(c => {
      if (c.component_type === 'bundle') return false;
      if (c.id === component?.id) return false;
      // Allow if not in any bundle, or if already in this bundle
      if (!c.bundle_id) return true;
      if (isEditMode && c.bundle_id === component.id) return true;
      return false;
    });
  }, [tripComponents, component, isEditMode]);

  // Components in *other* bundles (shown disabled)
  const otherBundledComponents = useMemo(() => {
    if (!tripComponents) return [];
    return tripComponents.filter(c => {
      if (c.component_type === 'bundle') return false;
      if (!c.bundle_id) return false;
      if (isEditMode && c.bundle_id === component.id) return false;
      return true;
    });
  }, [tripComponents, component, isEditMode]);

  // Map of bundle ids to bundle titles for the disabled label
  const bundleTitleMap = useMemo(() => {
    if (!tripComponents) return {};
    const map = {};
    tripComponents.filter(c => c.component_type === 'bundle').forEach(b => {
      map[b.id] = b.title;
    });
    return map;
  }, [tripComponents]);

  const toggleChild = (id) => {
    setSelectedChildIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      let startDatetime = null;
      // If no date is provided, treat the bundle as date TBD regardless of checkbox
      const effectiveDateIsTbd = dateIsTbd || !startDate;
      const effectiveTimeIsTbd = timeIsTbd || !startTime;
      if (!effectiveDateIsTbd && startDate) {
        startDatetime = startTime && !effectiveTimeIsTbd
          ? `${startDate}T${startTime}:00`
          : `${startDate}T00:00:00`;
      }

      const componentPayload = {
        component_type: 'bundle',
        title: title.trim(),
        description: description.trim() || null,
        start_datetime: startDatetime,
        date_is_tbd: effectiveDateIsTbd,
        time_is_tbd: effectiveTimeIsTbd,
        status: 'confirmed',
        component_data: {
          child_component_ids: [...selectedChildIds],
        },
      };

      let bundleId;

      if (isEditMode) {
        // Update existing bundle
        await axios.put(
          `${apiBase}/trips/${tripId}/components/${component.id}`,
          componentPayload,
          { headers }
        );
        bundleId = component.id;
      } else {
        // Create new bundle
        const res = await axios.post(
          `${apiBase}/trips/${tripId}/components`,
          componentPayload,
          { headers }
        );
        bundleId = res.data.component?.id;
      }

      // Set bundle members if any selected
      if (bundleId && selectedChildIds.size > 0) {
        await axios.put(
          `${apiBase}/trips/${tripId}/bundles/${bundleId}/members`,
          { component_ids: [...selectedChildIds] },
          { headers }
        );
      } else if (bundleId && isEditMode && selectedChildIds.size === 0) {
        // Clear all members in edit mode
        await axios.put(
          `${apiBase}/trips/${tripId}/bundles/${bundleId}/members`,
          { component_ids: [] },
          { headers }
        );
      }

      onSuccess && onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(`Failed to ${isEditMode ? 'update' : 'create'} bundle: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderComponentRow = (comp, { disabled = false, disabledLabel = '' } = {}) => {
    const Icon = COMPONENT_ICONS[comp.component_type] || CalendarIcon;
    const color = COMPONENT_COLORS[comp.component_type] || 'text-ui-ink-3';
    const isChecked = selectedChildIds.has(comp.id);

    return (
      <label
        key={comp.id}
        className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
          disabled
            ? 'border-ui-divider bg-ui-sunk opacity-50 cursor-not-allowed'
            : isChecked
              ? 'border-ui-violet-line bg-ui-violet-bg cursor-pointer'
              : 'border-ui-line bg-ui-surface hover:border-ui-control cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={disabled}
          onChange={() => !disabled && toggleChild(comp.id)}
          className="rounded border-ui-control text-ui-violet-fg focus:ring-ui-violet-solid"
        />
        <div className={`w-8 h-8 rounded-lg bg-ui-hover flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ui-ink truncate">{comp.title}</div>
          {comp.start_datetime && (
            <div className="text-xs text-ui-muted truncate">
              {new Date(comp.start_datetime).toLocaleDateString()}
            </div>
          )}
          {disabled && disabledLabel && (
            <div className="text-xs text-ui-muted italic">{disabledLabel}</div>
          )}
        </div>
      </label>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-ui-violet-strong flex items-center justify-center">
          <FolderOpen className="w-6 h-6 text-ui-on-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-ui-ink">
            {isEditMode ? 'Edit Bundle' : 'Create Bundle'}
          </h3>
          <p className="text-sm text-ui-muted">Group related items together</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-ui-red-bg border border-ui-red-line rounded-lg">
          <AlertCircle className="w-4 h-4 text-ui-red-fg flex-shrink-0" />
          <span className="text-sm text-ui-red-fg">{error}</span>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-ui-ink-2 mb-1">Title *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Dinner at Le Bernardin"
          className="w-full px-3 py-2 border border-ui-control rounded-lg focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid text-sm"
          autoFocus
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-ui-ink-2 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description..."
          rows={2}
          className="w-full px-3 py-2 border border-ui-control rounded-lg focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid text-sm resize-none"
        />
      </div>

      {/* Date / Time */}
      <div>
        <label className="block text-sm font-medium text-ui-ink-2 mb-1">Date & Time</label>
        <div className="flex items-center gap-2 mb-2">
          <label className="flex items-center gap-1.5 text-xs text-ui-muted cursor-pointer">
            <input
              type="checkbox"
              checked={dateIsTbd}
              onChange={(e) => setDateIsTbd(e.target.checked)}
              className="rounded border-ui-control text-ui-violet-fg focus:ring-ui-violet-solid"
            />
            Date TBD
          </label>
          <label className="flex items-center gap-1.5 text-xs text-ui-muted cursor-pointer">
            <input
              type="checkbox"
              checked={timeIsTbd}
              onChange={(e) => setTimeIsTbd(e.target.checked)}
              className="rounded border-ui-control text-ui-violet-fg focus:ring-ui-violet-solid"
            />
            Time TBD
          </label>
        </div>
        {!dateIsTbd && (
          <div className="flex gap-3">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-ui-control rounded-lg focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid text-sm"
            />
            {!timeIsTbd && (
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-32 px-3 py-2 border border-ui-control rounded-lg focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid text-sm"
              />
            )}
          </div>
        )}
      </div>

      {/* Component Picker */}
      <div>
        <label className="block text-sm font-medium text-ui-ink-2 mb-1">
          Bundle Items
          {selectedChildIds.size > 0 && (
            <span className="ml-2 text-xs font-normal text-ui-violet-fg">
              {selectedChildIds.size} selected
            </span>
          )}
        </label>
        <p className="text-xs text-ui-muted mb-3">
          Select components to include in this bundle. Items can also be added later.
        </p>

        {availableComponents.length === 0 && otherBundledComponents.length === 0 ? (
          <div className="text-sm text-ui-muted text-center py-6 bg-ui-sunk rounded-lg">
            No components available to bundle
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {availableComponents.map(c => renderComponentRow(c))}
            {otherBundledComponents.map(c =>
              renderComponentRow(c, {
                disabled: true,
                disabledLabel: `In: ${bundleTitleMap[c.bundle_id] || 'Another bundle'}`,
              })
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-ui-line">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-ui-ink-3 border border-ui-control rounded-lg hover:bg-ui-hover transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !title.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm text-ui-on-primary bg-ui-violet-strong rounded-lg hover:bg-ui-violet-strong/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {isEditMode ? 'Save Changes' : 'Create Bundle'}
        </button>
      </div>
    </div>
  );
};

export default BundleComponentForm;
